import React, { useState } from 'react';
import ApiConfigurationForm from './ApiConfigurationForm';
import FormModal from '@shared/ui/FormModal';

// Import the ApiConfiguration type
interface ApiConfiguration {
  authentication: {
    type: 'none' | 'api_key' | 'bearer_token' | 'oauth' | 'basic_auth' | 'custom';
    credentials: {
      api_key?: string;
      api_secret?: string;
      bearer_token?: string;
      username?: string;
      password?: string;
      oauth_client_id?: string;
      oauth_client_secret?: string;
    };
    headers?: Record<string, string>;
  };
  endpoint: {
    base_url: string;
    version?: string;
    endpoints?: Record<string, string>;
  };
  rate_limits: {
    requests_per_hour?: number;
    requests_per_minute?: number;
    burst_limit?: number;
  };
  data_mapping?: Record<string, string>;
  filters?: Record<string, string>;
}

interface AddDataSourceModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: any) => void;
}

const AddDataSourceModal: React.FC<AddDataSourceModalProps> = ({ isOpen, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    type: 'CSV Upload',
    schedule: 'Daily',
    status: 'Active',
    config: {}
  });

  const [apiConfig, setApiConfig] = useState<ApiConfiguration | undefined>(undefined);

  const handleSubmit = () => {
    const submissionData = {
      ...formData,
      config: isApiType(formData.type) && apiConfig 
        ? { ...formData.config, api: apiConfig }
        : formData.config
    };
    
    onSubmit(submissionData);
    onClose();
    setFormData({
      name: '',
      description: '',
      type: 'CSV Upload',
      schedule: 'Daily',
      status: 'Active',
      config: {}
    });
    setApiConfig(undefined);
  };

  const isApiType = (type: string) => {
    return ['Real-time API', 'Batch API', 'REST API', 'GraphQL API', 'Government API', 'Third-party API'].includes(type);
  };

  const handleTypeChange = (type: string) => {
    setFormData({ ...formData, type });
    if (!isApiType(type)) {
      setApiConfig(undefined);
    }
  };

  const isApi = isApiType(formData.type);

  return (
    <FormModal
      isOpen={isOpen}
      title="Add data source"
      onClose={onClose}
      onSubmit={handleSubmit}
      /* The API branch adds a whole configuration form; the file branch is five fields. */
      size={isApi ? 'xl' : 'md'}
      submitLabel="Create source"
      isDirty={formData.name.trim().length > 0 || formData.description.trim().length > 0}
    >
      <div className="space-y-4">
        <label className="block">
          <span className="mb-1 block text-sm font-medium text-text-secondary dark:text-text-dark-secondary">
            Name *
          </span>
          <input
            type="text"
            required
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            className="input"
            placeholder="Enter data source name"
          />
        </label>

        <label className="block">
          <span className="mb-1 block text-sm font-medium text-text-secondary dark:text-text-dark-secondary">
            Description
          </span>
          <textarea
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            className="input"
            rows={3}
            placeholder="Enter description"
          />
        </label>

        <label className="block">
          <span className="mb-1 block text-sm font-medium text-text-secondary dark:text-text-dark-secondary">
            Type *
          </span>
          <select
            required
            value={formData.type}
            onChange={(e) => handleTypeChange(e.target.value)}
            className="input"
          >
            <optgroup label="File-based sources">
              <option value="CSV Upload">CSV Upload</option>
              <option value="Web Scraper">Web Scraper</option>
              <option value="Other">Other</option>
            </optgroup>
            <optgroup label="API sources">
              <option value="Government API">Government API</option>
              <option value="Real-time API">Real-time API</option>
              <option value="Batch API">Batch API</option>
              <option value="REST API">REST API</option>
              <option value="GraphQL API">GraphQL API</option>
              <option value="Third-party API">Third-party API</option>
            </optgroup>
          </select>
        </label>

        <label className="block">
          <span className="mb-1 block text-sm font-medium text-text-secondary dark:text-text-dark-secondary">
            Schedule *
          </span>
          <select
            required
            value={formData.schedule}
            onChange={(e) => setFormData({ ...formData, schedule: e.target.value })}
            className="input"
          >
            <option value="Manual">Manual</option>
            <option value="Hourly">Hourly</option>
            <option value="Daily">Daily</option>
            <option value="Weekly">Weekly</option>
            <option value="Monthly">Monthly</option>
          </select>
        </label>

        <label className="block">
          <span className="mb-1 block text-sm font-medium text-text-secondary dark:text-text-dark-secondary">
            Status *
          </span>
          <select
            required
            value={formData.status}
            onChange={(e) => setFormData({ ...formData, status: e.target.value })}
            className="input"
          >
            <option value="Active">Active</option>
            <option value="Paused">Paused</option>
            <option value="Error">Error</option>
          </select>
        </label>

        {isApi && (
          <div className="border-t border-border-light pt-6 dark:border-border-dark-hairline">
            <h3 className="mb-4 text-base font-medium text-text-primary dark:text-text-dark-primary">
              API configuration
            </h3>
            <ApiConfigurationForm
              initialConfig={apiConfig}
              onChange={setApiConfig}
              dataSourceType={formData.type}
            />
          </div>
        )}
      </div>
    </FormModal>
  );
};

export default AddDataSourceModal;
